import type { AxiosProgressEvent, AxiosResponse } from 'axios';
import * as React from 'react';

import { UPLOAD_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import { useUploadFile } from '@/components/UploadFileModal/hooks';

export const useInitUploadComplete = () => {
  const { mutateAsync: uploadFile } = useUploadFile();

  const init = React.useCallback(
    async (
      { files, source, report_type }: InitRequestPayloadType,
      {
        onInitSuccess,
        onInitError,
      }: Pick<CallbacksType, 'onInitError' | 'onInitSuccess'>
    ) => {
      try {
        for (const file of files) {
          if (!file.id) {
            throw new Error(
              'id field is required for multiple document upload'
            );
          }
        }

        const { data } = await axiosInstance.post<InitApiResponseType>(
          UPLOAD_API_ROUTES.init(),
          {
            source,
            files,
            report_type,
          }
        );

        onInitSuccess?.(data);
        return data.upload_configs;
      } catch (error) {
        onInitError?.(error as Error);
      }
    },
    []
  );

  const uploadFileToBucket = React.useCallback(
    async (
      document: DocumentType,
      uploadConfig: UploadConfig,
      {
        onUploadSuccess,
        onUploadError,
        onUploadProgress,
      }: Pick<
        CallbacksType,
        'onUploadError' | 'onUploadProgress' | 'onUploadSuccess'
      >
    ) => {
      try {
        const { data } = (await uploadFile({
          uploadUrl: uploadConfig.upload_link,
          file: document.file,
          metadataFields: uploadConfig.metadata_fields,
          headers: uploadConfig.headers,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            onUploadProgress?.(percentCompleted, document);
          },
        })) as AxiosResponse<UploadResponseType>;
        onUploadSuccess?.(data, document, uploadConfig);
      } catch (error) {
        onUploadError?.(error as Error, document);
      }
    },
    [uploadFile]
  );

  const uploadComplete = React.useCallback(
    async (
      {
        files,
        feed_id,
        source,
        report_name,
        report_type,
      }: CompleteRequestPayloadType,
      {
        onUploadCompleteSuccess,
        onUploadCompleteError,
      }: Pick<
        CallbacksType,
        'onUploadCompleteError' | 'onUploadCompleteSuccess'
      >
    ) => {
      try {
        const method = feed_id ? 'PUT' : 'POST';
        const { data } = await axiosInstance.request<CompleteResponseType>({
          url: UPLOAD_API_ROUTES.complete(),
          method,
          data: {
            files,
            ...(feed_id
              ? {
                  feed_id,
                  action: 'add',
                }
              : {}),
            ...(source ? { source } : {}),
            ...(report_name ? { report_name } : {}),
            ...(report_type ? { report_type } : {}),
          },
        });

        onUploadCompleteSuccess?.(data);
        return data;
      } catch (error) {
        onUploadCompleteError?.(error as Error);
      }
    },
    []
  );

  const removeFile = React.useCallback(
    async (
      { source, files, feed_id, report_type }: RemoveFileType,
      {
        onRemoveSuccess,
        onRemoveError,
      }: Pick<CallbacksType, 'onRemoveError' | 'onRemoveSuccess'>
    ) => {
      try {
        const filesWithMetadata = files.map((file) => {
          return {
            bucket: file.bucket,
            key: file.key,
            metadata: {
              name: file.name,
              mime_type: file.mime_type,
              id: file.id,
            },
          };
        });

        const { data } = await axiosInstance.put<CompleteResponseType>(
          UPLOAD_API_ROUTES.complete(),
          {
            files: filesWithMetadata,
            action: 'remove',
            source,
            feed_id,
            report_type,
          }
        );

        onRemoveSuccess?.(data);
        return data;
      } catch (error) {
        onRemoveError?.(error as Error, {
          source,
          files,
          feed_id,
        });
      }
    },
    []
  );

  const initUpload = React.useCallback(
    async (
      { documents, source, report_type }: InitUpload,
      {
        onInitSuccess,
        onInitError,
        onUploadSuccess,
        onUploadError,
        onUploadProgress,
      }: Pick<
        CallbacksType,
        | 'onInitError'
        | 'onInitSuccess'
        | 'onUploadError'
        | 'onUploadProgress'
        | 'onUploadSuccess'
      > = {}
    ) => {
      if (documents.length === 0) {
        return null;
      }

      const uploadConfigs = await init(
        {
          files: documents.map(({ id, name, mime_type }) => ({
            id,
            name,
            mime_type,
          })),
          source,
          report_type,
        },
        {
          onInitSuccess,
          onInitError,
        }
      );

      if (!uploadConfigs) {
        return null;
      }

      const uploadConfigMap = new Map(
        uploadConfigs.map((config) => [config.id, config])
      );

      await Promise.all(
        documents.map((doc) => {
          const uploadConfig = uploadConfigMap.get(doc.id || '');

          if (!uploadConfig) {
            throw new Error(
              `Upload config not found for document with id: ${doc.id}`
            );
          }

          return uploadFileToBucket(doc, uploadConfig, {
            onUploadSuccess,
            onUploadError,
            onUploadProgress,
          });
        })
      );

      return documents.map((doc) => {
        const uploadConfig = uploadConfigMap.get(doc.id || '') as UploadConfig;
        return {
          bucket: uploadConfig.metadata_fields.bucket,
          key: uploadConfig.metadata_fields.key,
          metadata: {
            name: doc.name,
            mime_type: doc.mime_type,
            id: doc.id,
          },
        };
      });
    },
    [init, uploadFileToBucket]
  );

  const initUploadComplete = React.useCallback(
    async (
      {
        documents,
        feed_id,
        source,
        report_name,
        report_type,
      }: InitUploadCompleteType,
      callbacks: CallbacksType = {}
    ) => {
      const {
        onUploadCompleteSuccess,
        onUploadCompleteError,
        ...uploadCallbacks
      } = callbacks;

      const configuredFiles = await initUpload(
        { documents, source, report_type },
        uploadCallbacks
      );

      if (!configuredFiles) {
        return;
      }

      return await uploadComplete(
        {
          files: configuredFiles,
          feed_id,
          source,
          report_name,
          report_type,
        },
        {
          onUploadCompleteSuccess,
          onUploadCompleteError,
        }
      );
    },
    [initUpload, uploadComplete]
  );

  return { initUploadComplete, initUpload, uploadComplete, removeFile };
};

type UploadSourceType = 'ask_needl' | 'report';
type InitFileType = {
  id: string;
  name: string;
  mime_type: string;
};

type InitRequestPayloadType = {
  files: InitFileType[];
  source: UploadSourceType;
  report_type?: string;
};

type InitApiResponseType = {
  upload_configs: UploadConfig[];
};

type UploadResponseType = Record<string, unknown>;

type UploadConfig = {
  id: string;
  name: string;
  mime_type: string;
  metadata_fields: Record<string, unknown> & {
    bucket: string;
    key: string;
  };
  upload_link: string;
  headers?: Record<string, string>;
};

export type DocumentType = {
  id: string;
  name: string;
  mime_type: string;
  file: File;
};

type CompleteFileType = {
  bucket: string;
  key: string;
  metadata: {
    name: string;
    mime_type: string;
    id: string;
    web_url?: string;
  };
};

type CompleteRequestPayloadType = {
  files: CompleteFileType[];
  feed_id?: string;
  report_type?: string;
  report_name?: string;
  source?: UploadSourceType;
};

type CompleteResponseType = {
  feed_id?: string;
  report_id?: string;
  message?: string;
};

type RemoveFileType = {
  source: UploadSourceType;
  feed_id: string;
  report_type?: string;
  files: (InitFileType & { bucket: string; key: string })[];
};

type InitUpload = {
  documents: DocumentType[];
  source: UploadSourceType;
  report_type?: string;
};

type InitUploadCompleteType = InitUpload & {
  feed_id?: string;
  report_name?: string;
};

export type CallbacksType = {
  onInitSuccess?: (response: InitApiResponseType) => void;
  onInitError?: (error: Error) => void;
  onUploadSuccess?: (
    response: UploadResponseType,
    document: DocumentType,
    uploadConfig: UploadConfig
  ) => void;
  onUploadError?: (error: Error, document: DocumentType) => void;
  onUploadCompleteSuccess?: (response: CompleteResponseType) => void;
  onUploadCompleteError?: (error: Error) => void;
  onUploadProgress?: (percentProgress: number, document: DocumentType) => void;
  onRemoveSuccess?: (response: CompleteResponseType) => void;
  onRemoveError?: (error: Error, payload: RemoveFileType) => void;
};
